import {t, useLanguage} from '../i18n';
import './BenefitsCompact.css';
import BenefitsLoading from "../components/BenefitsLoading";
// LOOP LOYALTY CACHE SAFETY R6 — rejects degraded responses, avoids false-empty cache, and expires empty results quickly.
// src/pages/MyLoyaltyPage.jsx
// LOOP APPLE WALLET DELIVERY R5A1 — opens signed .pkpass links through the native iOS browser.
// LOOP WALLET SOURCE CUSTOMER R4E2 — use the exact CRM customer document that owns each loyalty account.
// LOOP WALLET AUTO SYNC UI R4E1 — accurate provider states and actionable errors.
// LOOP WALLET GOOGLE HTTP R4A1 — native-safe Google Wallet save link and clear setup status.
// LOOP D2.9.4H.3 — Loyalty mobile responsive polish
// LOOP D2.9.4H.2 — Loyalty exact visual follow-up / production polish
// LOOP D1.6 — Client-facing loyalty dashboard
import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Browser } from "@capacitor/browser";
import { auth } from "../firebase";
import { isIosCapacitor, httpRequestJson } from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import { callCallableNativeSafe } from "../utils/callableNativeSafe";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, RefreshCw, Gift, Store, CheckCircle2, Sparkles, QrCode, X, Clock3, MoreHorizontal, Wallet, ArrowUpRight } from "lucide-react";

const LOOP_LOYALTY_CACHE_KEY = "loop_my_loyalty_cache_v2";
const LOOP_LOYALTY_LEGACY_CACHE_KEY = "loop_my_loyalty_cache_v1";
const LOOP_BENEFITS_CACHE_TTL_MS = 1000 * 60 * 10;
const LOOP_EMPTY_BENEFITS_CACHE_TTL_MS = 1000 * 60;

const LOOP_REDEMPTION_QR_LINK_MODE = "smart_web"; // production: smart_web | web | custom_scheme
const LOOP_PUBLIC_WEB_ORIGIN = "https://www.loopreservas.com";

const LOOP_WALLET_FUNCTIONS_BASE = "https://us-central1-web-app-d9799.cloudfunctions.net";
const LOOP_WALLET_CREATE_HTTP_URL = `${LOOP_WALLET_FUNCTIONS_BASE}/createLoyaltyWalletPassHttpR4A1`;

function withTimeoutR4A1(promise, ms, label = "wallet-timeout") {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try { if (timer) clearTimeout(timer); } catch (_) {}
    }),
    timeout,
  ]);
}

async function getWalletIdTokenR4A1() {
  try {
    const manual = getManualAuth();
    if (manual && manual.idToken && isManualAuthLikelyValid(manual)) {
      return String(manual.idToken);
    }
  } catch (_) {}

  try {
    const user = auth && auth.currentUser ? auth.currentUser : null;
    if (user && typeof user.getIdToken === "function") {
      return await user.getIdToken(false);
    }
  } catch (_) {}

  return "";
}

function walletFriendlyErrorR4A1(error) {
  const serverError = error?.customData?.serverResponse?.error || error?.details?.error || {};
  const parts = [
    error?.message,
    error?.code,
    error?.details?.message,
    error?.details?.operation,
    serverError?.message,
    serverError?.status,
    serverError?.details?.operation,
    serverError?.details?.googleCode,
  ].filter(Boolean);
  const raw = parts.join(" ").toLowerCase();

  if (raw.includes("wallet-auth-missing") || raw.includes("unauthenticated") || raw.includes("401")) {
    return "Inicia sesión otra vez para agregar esta tarjeta a Wallet.";
  }
  if (raw.includes("permission-denied") || raw.includes("403")) {
    return "No pudimos confirmar que esta tarjeta pertenece a tu cuenta.";
  }
  if (raw.includes("not-found") || raw.includes("404")) {
    return "No encontramos esta tarjeta de fidelidad.";
  }
  if (raw.includes("google_wallet_config_missing") || raw.includes("private_key") || raw.includes("service_account")) {
    return "Google Wallet todavía requiere configuración del negocio.";
  }
  if (raw.includes("invalid_grant") || raw.includes("account not found") || raw.includes("oauth_token")) {
    return "Google Wallet no pudo autenticar la cuenta emisora. Intenta nuevamente más tarde.";
  }
  if (raw.includes("failed-precondition") || raw.includes("412")) {
    return "Este negocio todavía no tiene un programa de fidelidad listo para Wallet.";
  }
  if (raw.includes("timeout")) {
    return "Preparar Wallet tardó demasiado. Revisa tu conexión e intenta nuevamente.";
  }
  return "No pudimos preparar Google Wallet. Intenta nuevamente.";
}


function appendUrlParams(url, params = {}) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim()) {
        parsed.searchParams.set(key, String(value));
      }
    });
    return parsed.toString();
  } catch (_) {
    const entries = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && String(value).trim())
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    if (!entries.length) return raw;
    return `${raw}${raw.includes("?") ? "&" : "?"}${entries.join("&")}`;
  }
}

function buildLoopRedemptionLinks(data = {}) {
  const rawToken = String(data?.token || data?.redemptionToken || "").trim();
  const backendUrl = String(data?.qrUrl || data?.webUrl || data?.url || "").trim();
  let token = rawToken;

  if (!token && backendUrl) {
    try {
      const parsed = new URL(backendUrl);
      const parts = parsed.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((part) => part === "checkin");
      if (idx >= 0 && parts[idx + 1]) token = decodeURIComponent(parts[idx + 1]);
    } catch (_) {}
  }

  const encodedToken = encodeURIComponent(token);
  const baseWebUrl = backendUrl || (token ? `${LOOP_PUBLIC_WEB_ORIGIN}/loyalty/checkin/${encodedToken}` : "");
  const smartWebUrl = token ? appendUrlParams(baseWebUrl, { open: "app", source: "loyalty_qr" }) : baseWebUrl;
  const webUrl = LOOP_REDEMPTION_QR_LINK_MODE === "smart_web" ? smartWebUrl : baseWebUrl;
  const appUrl = token ? `loopreservas://loyalty/checkin/${encodedToken}` : "";

  return {
    token,
    baseWebUrl,
    webUrl,
    smartWebUrl,
    appUrl,
    qrValue: LOOP_REDEMPTION_QR_LINK_MODE === "custom_scheme" && appUrl ? appUrl : webUrl,
  };
}


function loyaltyCacheOwner() {
  const manual = getManualAuth();
  return auth?.currentUser?.uid || (isManualAuthLikelyValid(manual) ? manual?.uid || manual?.localId || "" : "");
}
function readLoyaltyCache() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    try { window.localStorage.removeItem(LOOP_LOYALTY_LEGACY_CACHE_KEY); } catch (_) {}
    const raw = window.localStorage.getItem(LOOP_LOYALTY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const owner = loyaltyCacheOwner();
    if (!owner || parsed.owner !== owner) return null;
    const savedAt = Number(parsed?.savedAt || 0);
    const payload = parsed?.payload || null;
    const hasRows = Array.isArray(payload?.businesses) && payload.businesses.length > 0;
    if (!payload || payload.ok !== true || payload.degraded === true) return null;
    const ttl = hasRows ? LOOP_BENEFITS_CACHE_TTL_MS : LOOP_EMPTY_BENEFITS_CACHE_TTL_MS;
    if (!savedAt || Date.now() - savedAt > ttl) return hasRows ? { payload, stale: true } : null;
    return { payload, stale: false };
  } catch (_) {
    return null;
  }
}

function writeLoyaltyCache(payload = null) {
  try {
    if (typeof window === "undefined" || !window.localStorage || !payload) return;
    if (payload.ok !== true || payload.degraded === true) return;
    try { window.localStorage.removeItem(LOOP_LOYALTY_LEGACY_CACHE_KEY); } catch (_) {}
    window.localStorage.setItem(LOOP_LOYALTY_CACHE_KEY, JSON.stringify({ payload, owner: loyaltyCacheOwner(), savedAt: Date.now() }));
  } catch (_) {}
}

function fmtDate(value) {
  try {
    if (!value) return "Sin actividad";
    const d = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "Sin actividad";
    return d.toLocaleDateString("es-PA", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) {
    return "Sin actividad";
  }
}


function fmtExpiry(value) {
  try {
    if (!value) return "Expira pronto";
    const d = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "Expira pronto";
    return d.toLocaleTimeString("es-PA", { hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "Expira pronto";
  }
}

function initials(name) {
  const parts = String(name || "Loop").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return (parts[0] || "L").slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function loyaltyProgressValues(account = {}, program = {}) {
  const isPoints = program?.type === "points";
  const required = Math.max(1, Number(isPoints ? (program?.pointsRequired || account?.pointsRequired || 100) : (program?.stampsRequired || account?.stampsRequired || 5)));
  const balance = Math.max(0, Number(isPoints ? (account?.pointsBalance || 0) : (account?.stampsBalance || 0)));
  const unit = isPoints ? "punto" : "sello";
  return { isPoints, required, balance, unit };
}

function statusText(account = {}, program = {}) {
  const { required, balance, unit } = loyaltyProgressValues(account, program);
  const rewards = Math.max(0, Number(account?.rewardsAvailable || 0));
  if (rewards > 0) return t(rewards === 1 ? "{count} recompensa disponible" : "{count} recompensas disponibles",{count:rewards});
  const missing = Math.max(0, required - balance);
  return missing > 0 ? t("Faltan {count} {unit}",{count:missing,unit:t(unit+(missing===1?"":"s"))}) : t("Lista para recompensa");
}

function StampMark({ logoUrl }) {
  const [fallback, setFallback] = useState(false);
  return logoUrl && !fallback ? <img className="mlp-stamp-logo" src={logoUrl} alt="" draggable="false"
    onError={() => setFallback(true)} onLoad={event => {
      const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
      if (width / height > 2 || height / width > 2) setFallback(true);
    }} /> : <CheckCircle2 size={24} strokeWidth={1.5} />;
}

function StampGrid({ balance, required, rewards, logoUrl = "" }) {
  const safeRequired = Math.max(1, Math.round(Number(required || 5)));
  const safeBalance = Math.max(0, Math.floor(Number(balance || 0)));
  const cappedBalance = Math.max(0, Math.min(safeBalance, safeRequired));
  const rewardReady = Number(rewards || 0) > 0 || cappedBalance >= safeRequired;
  const visibleSlots = safeRequired <= 10 ? safeRequired : 10;
  const hiddenSlots = Math.max(0, safeRequired - visibleSlots);

  return (
    <div className={`mlp-stamp-grid ${visibleSlots > 5 ? "is-two-rows" : ""} ${rewardReady ? "has-reward-ready" : ""}`} aria-label={t("{balance} de {required} sellos",{balance:cappedBalance,required:safeRequired})}>
      {Array.from({ length: visibleSlots }).map((_, idx) => {
        const stampNumber = idx + 1;
        const filled = stampNumber <= Math.min(cappedBalance, visibleSlots);
        return (
          <span
            key={`stamp-${idx}`}
            className={`mlp-stamp ${filled ? "is-filled" : ""} ${filled && logoUrl ? "has-logo" : ""}`}
            title={t("Sello {number} de {total}", {number: stampNumber, total: safeRequired})}
            style={{ "--stamp-index": idx }}
          >
            {filled && logoUrl ? (
              <StampMark logoUrl={logoUrl} />
            ) : filled ? (
              <b>✓</b>
            ) : (
              <small>{stampNumber}</small>
            )}
          </span>
        );
      })}
      {hiddenSlots > 0 ? <em>{safeBalance > visibleSlots ? `${Math.min(safeBalance, safeRequired)}/${safeRequired}` : `${hiddenSlots} más`}</em> : null}
    </div>
  );
}

function PointMilestones({ balance, required }) {
  const safeRequired = Math.max(1, Number(required || 100));
  const safeBalance = Math.max(0, Number(balance || 0));
  const pct = Math.max(0, Math.min(100, Math.round((safeBalance / safeRequired) * 100)));
  const marks = [0, 25, 50, 75, 100];

  return (
    <div className="mlp-point-pass" aria-label={`${safeBalance} de ${safeRequired} puntos`}>
      <div className="mlp-point-track"><i style={{ width: `${pct}%` }} /></div>
      <div className="mlp-point-marks">
        {marks.map((mark) => (
          <span key={mark} className={pct >= mark ? "is-active" : ""}>{mark === 100 ? safeRequired : Math.round((safeRequired * mark) / 100)}</span>
        ))}
      </div>
    </div>
  );
}

function ProgramRow({ item, logoUrl = "", onUseReward = null }) {
  const program = item?.program || {};
  const account = item?.account || {};
  const { isPoints, required, balance } = loyaltyProgressValues(account, program);
  const rewards = Math.max(0, Number(account.rewardsAvailable || 0));
  const missing = Math.max(0, required - balance);
  const unit = t(isPoints ? "puntos" : "sellos");
  return <div className={`mlp-program mlp-pass-program ${isPoints ? "is-points" : "is-stamps"}`}>
    <p className="loyalty-eyebrow">{t("Tu próxima recompensa")}</p>
    <h3 className="mlp-reward-title">{program.rewardLabel || program.name || t("Una visita con premio")}</h3>
    <div className="loyalty-progress-heading"><strong>{balance} <span>de {required} {unit}</span></strong></div>
    {isPoints ? <PointMilestones balance={balance} required={required} /> : <StampGrid balance={balance} required={required} rewards={rewards} logoUrl={logoUrl} />}
    <p className="mlp-program-note">{balance === 0 ? t("Tu próxima visita es el comienzo.") : missing > 0 ? t("Te faltan {count} {unit} para tu próxima recompensa.",{count:missing,unit}) : t("Completaste tu tarjeta.")}</p>
    {account.recoveryBalance > 0 && <p className="loyalty-recovery-note">{t("Las próximas acumulaciones compensarán") + " "}{account.recoveryBalance} {unit}{" " + t("de una devolución anterior.")}</p>}
    {rewards > 0 && <div className="loyalty-reward-footer">
      <div className="loyalty-reward-summary"><span className="loyalty-gift"><Gift size={20} strokeWidth={1.6} /></span><div><strong>{t(rewards===1?"{count} recompensa disponible":"{count} recompensas disponibles",{count:rewards})}</strong><span>{t(rewards===1?"Ya puedes disfrutarla":"Ya puedes disfrutarlas")}</span></div></div>
      <button type="button" className="loyalty-redeem" onClick={() => onUseReward?.(item)}>{t("Usar recompensa")}<ArrowUpRight size={17} /></button>
    </div>}
  </div>;
}

function LoyaltyOptions({ businessName, onWallet, onBusiness }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 16 });
  const trigger = useRef(null);
  const panel = useRef(null);
  const close = () => { setOpen(false); trigger.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const mobile = window.matchMedia('(max-width: 700px)').matches;
    if (mobile) document.body.style.overflow = 'hidden';
    panel.current?.querySelector('button')?.focus();
    const keydown = event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key === 'Tab') {
        const buttons = [...(panel.current?.querySelectorAll('button') || [])];
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', keydown);
    const reposition = () => setOpen(false);
    window.addEventListener('resize', reposition);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keydown); window.removeEventListener('resize', reposition); };
  }, [open]);
  return <>
    <button ref={trigger} className="loyalty-options-trigger" type="button" aria-label={t("Opciones de {name}",{name:businessName})} aria-haspopup="dialog" aria-expanded={open} onClick={() => {
      const rect = trigger.current.getBoundingClientRect();
      setPosition({ top: Math.min(rect.bottom + 8, window.innerHeight - 210), right: Math.max(16, window.innerWidth - rect.right) });
      setOpen(true);
    }}><MoreHorizontal size={21} /></button>
    {open && createPortal(<div className="loyalty-options-overlay" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
      <section ref={panel} className="loyalty-options-panel" style={position} role="dialog" aria-modal="true" aria-label={t("Opciones de {name}",{name:businessName})}>
        <div className="loyalty-options-heading"><strong>{businessName}</strong><button type="button" aria-label={t("Cerrar opciones")} onClick={close}><X size={18} /></button></div>
        <button type="button" className="loyalty-option-row" onClick={() => { close(); onWallet(); }}><Wallet size={19} /><span>{t("Guardar en Wallet")}<small>{t("Lleva tu tarjeta contigo")}</small></span><ArrowUpRight size={16} /></button>
        <button type="button" className="loyalty-option-row" onClick={() => { close(); onBusiness(); }}><Store size={19} /><span>{t("Ver negocio")}<small>{t("Explora sus servicios")}</small></span><ArrowUpRight size={16} /></button>
      </section>
    </div>, document.body)}
  </>;
}


function getPrimaryWalletProgramItem(row = {}) {
  const items = Array.isArray(row.items) ? row.items : [];
  if (!items.length) return null;
  return [...items].sort((a, b) => {
    const aAccount = a?.account || {};
    const bAccount = b?.account || {};
    const aScore = Number(aAccount.rewardsAvailable || 0) * 100000 + Number(aAccount.stampsBalance || 0) * 1000 + Number(aAccount.pointsBalance || 0);
    const bScore = Number(bAccount.rewardsAvailable || 0) * 100000 + Number(bAccount.stampsBalance || 0) * 1000 + Number(bAccount.pointsBalance || 0);
    return bScore - aScore;
  })[0];
}

function getLoyaltyItemSourceCustomerId(row = {}, item = null) {
  return String(
    item?.sourceCustomerId ||
    item?.account?.sourceCustomerId ||
    item?.account?.customerId ||
    row?.customer?.id ||
    ""
  ).trim();
}

function BusinessLoyaltyCard({ row, onOpenBusiness, onUseReward, onAddWallet }) {
  const items = Array.isArray(row?.items) ? row.items : [];
  const rewards = Number(row?.totalRewardsAvailable || 0);
  const customer = row?.customer || {};
  const balance = Number(row.totalStampsBalance || 0) + Number(row.totalPointsBalance || 0);
  const logoUrl = row.logoUrl || row.logo || row.businessLogoUrl || row.business?.logoUrl || row.business?.logo || "";
  const primaryWalletItem = getPrimaryWalletProgramItem(row);

  return (
    <article className={`mlp-card mlp-loyalty-pass mlp-option-one-pass ${rewards > 0 ? "has-reward" : ""}`}>
      {logoUrl ? (
        <div className="mlp-card-watermark" style={{ backgroundImage: `url(${logoUrl})` }} aria-hidden="true" />
      ) : (
        <div className="mlp-card-watermark-text" aria-hidden="true">loop</div>
      )}

      <div className="mlp-card-head mlp-card-head-clean">
        <div className="mlp-business-logo" aria-hidden="true">
          {logoUrl ? <img src={logoUrl} alt="" /> : <span>loop</span>}
        </div>
        <div className="mlp-title">
          <span>{row.category || t("Tu negocio")}</span>
          <h2>{row.businessName || t("Negocio")}</h2>
          {customer.loyaltyUpdatedAt ? <p>{t("Última visita:") + " "}{fmtDate(customer.loyaltyUpdatedAt)}</p> : null}
        </div>
        <LoyaltyOptions businessName={row.businessName || "Negocio"} onWallet={() => onAddWallet?.(row, primaryWalletItem)} onBusiness={() => onOpenBusiness(row)} />
      </div>

      <div className={`mlp-pass-status ${rewards > 0 ? "is-ready" : ""}`}>
        <span>{rewards > 0 ? t(rewards === 1 ? "{count} recompensa disponible" : "{count} recompensas disponibles",{count:rewards}) : balance > 0 ? t("{count} sellos/puntos activos",{count:balance}) : t("Empieza a acumular en tu próxima reserva pagada")}</span>
        <small>{items.length}{" " + t("programa")}{items.length === 1 ? "" : "s"}{" " + t("activo")}{items.length === 1 ? "" : "s"}</small>
      </div>

      <div className="mlp-programs">
        {items.map((item) => <ProgramRow key={item.program?.id || item.account?.programId} item={item} logoUrl={logoUrl} onUseReward={(selectedItem) => onUseReward && onUseReward(row, selectedItem)} />)}
      </div>
    </article>
  );
}



async function openAppleWalletUrlR5A1(url) {
  const cleanUrl = String(url || "").trim();
  if (!cleanUrl) return;

  if (isIosCapacitor()) {
    try {
      await Browser.open({
        url: cleanUrl,
        presentationStyle: "popover",
      });
      return;
    } catch (error) {
      console.warn("[MyLoyaltyPage] Browser.open Apple Wallet failed", error);
    }
  }

  if (typeof window !== "undefined") {
    window.location.assign(cleanUrl);
  }
}


function LoyaltyWalletModal({ wallet, loading, error, onClose }) {
  if (!wallet) return null;

  const row = wallet.row || {};
  const businessName = row.businessName || "Negocio";
  const google = wallet.google || {};
  const apple = wallet.apple || {};
  const googleUrl = wallet.googleWalletUrl || google.saveUrl || "";
  const cardUrl = wallet.cardUrl || wallet.webUrl || "";
  const googleNeedsSetup = !googleUrl && google?.setupRequired === true && Array.isArray(google?.missing) && google.missing.length > 0;
  const googleDisabledLabel = loading
    ? "Preparando Google Wallet..."
    : error
      ? "Google Wallet no disponible"
      : googleNeedsSetup
        ? "Google Wallet requiere configuración"
        : "No pudimos preparar Google Wallet";

  return (
    <div className="mlp-redemption-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="mlp-redemption-modal mlp-wallet-modal" role="dialog" aria-modal="true" aria-label={t("Agregar a Wallet")} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="mlp-redemption-close" onClick={onClose} aria-label={t("Cerrar")}><X size={18} /></button>
        <div className="mlp-redemption-icon"><Gift size={25} /></div>
        <span className="mlp-redemption-eyebrow">{t("TARJETA DIGITAL")}</span>
        <h2>{t("Agregar a Wallet")}</h2>
        <p className="mlp-redemption-copy">{t("Guarda tu tarjeta de fidelidad de")}<strong>{businessName}</strong>{t(". El QR de Wallet abre el check-in del cliente en Loop.")}</p>

        <div className="mlp-wallet-actions-modal">
          {googleUrl ? (
            <a className="mlp-wallet-provider-btn is-google" href={googleUrl} target="_blank" rel="noreferrer">{t("Agregar a Google Wallet")}</a>
          ) : (
            <button type="button" className="mlp-wallet-provider-btn is-disabled" disabled>
              {googleDisabledLabel}
            </button>
          )}

          {apple?.ready && wallet.appleWalletUrl ? (
            <button
              type="button"
              className="mlp-wallet-provider-btn is-apple"
              onClick={() => openAppleWalletUrlR5A1(wallet.appleWalletUrl)}
            >{t("Agregar a Apple Wallet")}</button>
          ) : (
            <button type="button" className="mlp-wallet-provider-btn is-disabled" disabled>{t("Apple Wallet requiere certificados")}</button>
          )}
        </div>

        {cardUrl ? (
          <div className="mlp-wallet-card-preview">
            <QRCodeCanvas value={cardUrl} size={148} includeMargin />
            <small>QR estable de tarjeta. No vence como los QR temporales de canje.</small>
          </div>
        ) : null}

        {error ? <div className="mlp-redemption-error">{t(error)}</div> : null}

        {googleNeedsSetup ? (
          <p className="mlp-redemption-note">
            La tarjeta todavía necesita configuración del emisor de Google Wallet.
          </p>
        ) : null}

        {apple?.missing?.length ? (
          <p className="mlp-redemption-note">
            Para Apple Wallet faltan Pass Type ID y certificados. Cuando estén listos, activamos el botón .pkpass.
          </p>
        ) : null}

        {loading ? <div className="mlp-redemption-loading">{t("Preparando tarjeta...")}</div> : null}
      </section>
    </div>
  );
}


function LoyaltyRedemptionModal({ redemption, loading, error, onClose }) {
  if (!redemption) return null;

  const url = redemption.qrValue || redemption.smartWebUrl || redemption.webUrl || redemption.url || "";
  const appUrl = redemption.appUrl || "";
  const rewardLabel = redemption.rewardLabel || redemption.item?.program?.rewardLabel || "Recompensa";
  const businessName = redemption.row?.businessName || "Negocio";
  const expiresAt = redemption.expiresAt || redemption.expiresAtIso || null;

  return (
    <div className="mlp-redemption-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="mlp-redemption-modal" role="dialog" aria-modal="true" aria-label={t("Usar recompensa")} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="mlp-redemption-close" onClick={onClose} aria-label={t("Cerrar")}><X size={18} /></button>
        <div className="mlp-redemption-icon"><QrCode size={25} /></div>
        <span className="mlp-redemption-eyebrow">{t("QR seguro")}</span>
        <h2>{t("Usar recompensa")}</h2>
        <p className="mlp-redemption-copy">{t("Muéstrale este QR al negocio para confirmar")}<strong>{rewardLabel}</strong> en {businessName}.
        </p>

        <div className="mlp-redemption-qr">
          {url ? (
            <>
              <QRCodeCanvas value={url} size={210} includeMargin />
              <small className="mlp-redemption-link-mode">Escanéalo con la cámara. Si el negocio tiene Loop instalado, intentará abrir la app automáticamente.</small>
            </>
          ) : <span>{t("Generando QR...")}</span>}
        </div>

        <div className="mlp-redemption-meta">
          <Clock3 size={15} />
          <span>{expiresAt ? `Válido hasta ${fmtExpiry(expiresAt)}` : "Este QR expira pronto."}</span>
        </div>

        {error ? <div className="mlp-redemption-error">{t(error)}</div> : null}

        <p className="mlp-redemption-note">
          Por seguridad, el QR es temporal y solo puede usarse una vez. Si la app no abre, el negocio puede confirmar el canje desde esta misma página web.
        </p>

        {appUrl ? (
          <a className="mlp-redemption-open-app" href={appUrl}>
            Abrir manualmente en Loop
          </a>
        ) : null}

        {loading ? <div className="mlp-redemption-loading">{t("Preparando QR...")}</div> : null}
      </section>
    </div>
  );
}


export default function MyLoyaltyPage({ embedded = false } = {}) {
 useLanguage();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(() => readLoyaltyCache()?.payload || null);
  const [loading, setLoading] = useState(() => !readLoyaltyCache()?.payload);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedFromCache, setLoadedFromCache] = useState(() => Boolean(readLoyaltyCache()?.payload));
  const [error, setError] = useState("");
  const [redemptionModal, setRedemptionModal] = useState(null);
  const [redemptionLoading, setRedemptionLoading] = useState(false);
  const [redemptionError, setRedemptionError] = useState("");
  const [walletModal, setWalletModal] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState("");

  const rows = useMemo(() => Array.isArray(payload?.businesses) ? payload.businesses : [], [payload]);
  const totalRewards = useMemo(() => rows.reduce((sum, row) => sum + Number(row.totalRewardsAvailable || 0), 0), [rows]);

  const load = async ({ manual = false } = {}) => {
    const cached = readLoyaltyCache();
    const hasPayload = Boolean(payload || cached?.payload);
    setLoading(!hasPayload);
    setRefreshing(hasPayload);
    setError("");
    try {
      const res = await callCallableNativeSafe({
        functionName: "getMyLoyaltyPrograms",
        data: { limit: 50 },
        timeoutMs: 45000,
      });
      const nextPayload = res?.data || null;
      const nextRows = Array.isArray(nextPayload?.businesses) ? nextPayload.businesses : [];
      if (!nextPayload || nextPayload.ok !== true) {
        throw new Error("loyalty-invalid-response");
      }
      if (nextPayload.degraded === true && nextRows.length === 0) {
        throw new Error("loyalty-degraded-empty-response");
      }
      setPayload(nextPayload);
      writeLoyaltyCache(nextPayload);
      setLoadedFromCache(false);
      if (nextPayload.degraded === true) {
        setError("Mostramos los beneficios encontrados, pero una parte de la actualización no pudo completarse.");
      }
    } catch (e) {
      console.error("[MyLoyaltyPage] load failed", e);
      const code = String(e?.code || "").toLowerCase();
      const message = String(e?.message || "").toLowerCase();
      const detailsReason = String(e?.details?.reason || "").toLowerCase();
      const fallback = readLoyaltyCache();
      const fallbackHasRows = Array.isArray(fallback?.payload?.businesses) && fallback.payload.businesses.length > 0;
      if (fallbackHasRows) {
        setPayload(fallback.payload);
        setLoadedFromCache(true);
        setError(manual ? "No pudimos actualizar; mostrando tu último estado guardado." : "");
      } else if (code.includes("unauthenticated")) {
        setError("Inicia sesión para ver tu fidelidad.");
      } else if (
        message.includes("timeout") ||
        code.includes("deadline-exceeded")
      ) {
        setPayload(null);
        setError("La conexión tardó demasiado. Pulsa Actualizar para intentarlo nuevamente.");
      } else if (
        code.includes("failed-precondition") ||
        message.includes("índices") ||
        message.includes("indices") ||
        detailsReason.includes("identity")
      ) {
        setPayload(null);
        setError("Estamos terminando de preparar tus beneficios. Intenta actualizar nuevamente en unos minutos.");
      } else {
        setPayload(null);
        setError("No pudimos cargar tu fidelidad en este momento.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openBusiness = (row) => {
    const path = row?.publicPath || (row?.businessSlug ? `/business/${encodeURIComponent(row.businessSlug)}` : "");
    if (path) navigate(path);
    else if (row?.businessId) navigate(`/business/${encodeURIComponent(row.businessId)}`);
    else setError("No pudimos encontrar este negocio. Actualiza tus beneficios e intenta de nuevo.");
  };

  const createRedemptionQr = async (row, item) => {
    const businessId = row?.businessId;
    const customerId = getLoyaltyItemSourceCustomerId(row, item);
    const programId = item?.program?.id || item?.account?.programId;
    if (!businessId || !customerId || !programId) {
      setRedemptionError("No pudimos preparar esta recompensa.");
      setRedemptionModal({ row, item });
      return;
    }

    setRedemptionLoading(true);
    setRedemptionError("");
    setRedemptionModal({ row, item, rewardLabel: item?.program?.rewardLabel || "Recompensa" });

    try {
      const callable = httpsCallable(getFunctions(), "createLoyaltyRedemptionToken");
      const res = await callable({ businessId, customerId, programId, ttlMinutes: 10 });
      const data = res?.data || {};
      const links = buildLoopRedemptionLinks(data);
      setRedemptionModal({
        row,
        item,
        ...data,
        ...links,
        url: links.qrValue || links.appUrl || links.webUrl || data.url || "",
        qrValue: links.qrValue || links.appUrl || links.webUrl || data.url || "",
        rewardLabel: data.rewardLabel || item?.program?.rewardLabel || "Recompensa",
      });
    } catch (e) {
      console.error("[MyLoyaltyPage] create redemption QR failed", e);
      const msg = String(e?.message || "");
      setRedemptionError(msg || "No pudimos generar el QR. Intenta de nuevo.");
    } finally {
      setRedemptionLoading(false);
    }
  };


  const createWalletPass = async (row, item = null) => {
    const businessId = row?.businessId;
    const selectedItem = item || getPrimaryWalletProgramItem(row);
    const customerId = getLoyaltyItemSourceCustomerId(row, selectedItem);
    const programId = selectedItem?.program?.id || selectedItem?.account?.programId || "";
    if (!businessId || !customerId) {
      setWalletError("No pudimos preparar esta tarjeta.");
      setWalletModal({ row, item: selectedItem });
      return;
    }

    setWalletLoading(true);
    setWalletError("");
    setWalletModal({ row, item: selectedItem });

    try {
      let data = null;

      if (isIosCapacitor()) {
        const idToken = await getWalletIdTokenR4A1();
        if (!idToken) throw new Error("wallet-auth-missing");

        data = await withTimeoutR4A1(
          httpRequestJson({
            url: LOOP_WALLET_CREATE_HTTP_URL,
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
            data: {
              businessId,
              customerId,
              ...(programId ? { programId } : {}),
              source: "ios_loyalty_wallet_r4a1",
            },
          }),
          35000,
          "wallet-http-timeout"
        );
      } else {
        const callable = httpsCallable(getFunctions(), "createLoyaltyWalletPass");
        const res = await withTimeoutR4A1(callable({
          businessId,
          customerId,
          ...(programId ? { programId } : {}),
        }), 35000, "wallet-callable-timeout");
        data = res?.data || {};
      }

      setWalletModal({
        row,
        item: selectedItem,
        ...data,
      });
    } catch (e) {
      console.error("[MyLoyaltyPage] create wallet pass failed", e);
      setWalletError(walletFriendlyErrorR4A1(e));
    } finally {
      setWalletLoading(false);
    }
  };

  return (
    <div className={`mlp-page ${embedded ? "mlp-page-embedded" : ""}`}>
      <style>{styles}</style>
      <main className="mlp-shell">
        {!embedded ? <button type="button" className="mlp-back" onClick={() => navigate(-1)}><ArrowLeft size={16} />{" " + t("Volver")}</button> : null}

        {!embedded ? <section className="mlp-hero">
          <div>
            <span>{t("LOOP FIDELIDAD")}</span>
            <h1>{t("Mi fidelidad")}</h1>
            <p>{t("Consulta tus sellos, recompensas disponibles y programas activos en los negocios donde reservas.")}</p>
          </div>
          <button type="button" className="mlp-refresh" onClick={() => load({ manual: true })} disabled={refreshing}><RefreshCw size={16} /> {refreshing ? t("Actualizando...") : t("Actualizar")}</button>
        </section> : null}

        {totalRewards > 0 && <p className="benefits-reward-total"><Gift size={16}/>{" " + t("Tienes") + " "}{totalRewards}{" " + t("recompensas disponibles")}</p>}

        <BenefitsLoading busy={loading || refreshing} hasRows={rows.length > 0} error={error} onRetry={() => load({manual:true})} title={t("fidelidad")} />

        {!loading && !refreshing && !error && rows.length === 0 ? (
          <section className="mlp-empty">
            <div><Gift size={28} /></div>
            <h2>{t("Todavía no tienes programas de fidelidad")}</h2>
            <p>{t("Cuando reserves en negocios con fidelidad activa, tus sellos y recompensas aparecerán aquí.")}</p>
            <button type="button" onClick={() => navigate("/")}>{t("Explorar negocios")}</button>
          </section>
        ) : null}

        {rows.length > 0 ? (
          <section className="mlp-grid">
            {rows.map((row) => <BusinessLoyaltyCard key={`${row.businessId}-${row.customer?.id}`} row={row} onOpenBusiness={openBusiness} onUseReward={createRedemptionQr} onAddWallet={createWalletPass} />)}
          </section>
        ) : null}

        <LoyaltyWalletModal
          wallet={walletModal}
          loading={walletLoading}
          error={walletError}
          onClose={() => {
            setWalletModal(null);
            setWalletError("");
          }}
        />

        <LoyaltyRedemptionModal
          redemption={redemptionModal}
          loading={redemptionLoading}
          error={redemptionError}
          onClose={() => {
            setRedemptionModal(null);
            setRedemptionError("");
          }}
        />
      </main>
    </div>
  );
}

const styles = `
.mlp-page {
  min-height: 100vh;
  background: transparent;
  padding: 0 0 96px;
  color:#0f172a;
  box-sizing:border-box;
}
.mlp-shell { width: 100%; margin: 0 auto; }
.mlp-page:not(.mlp-page-embedded) .mlp-shell { width: min(1440px, calc(100vw - 64px)); }
.mlp-back {
  border:1px solid #dbe4ea;
  background:#fff;
  border-radius:999px;
  padding:10px 14px;
  display:inline-flex;
  align-items:center;
  gap:8px;
  font-weight:900;
  color:#334155;
  cursor:pointer;
  box-shadow:0 10px 24px rgba(15,23,42,.06);
  margin-bottom: 16px;
}
.mlp-hero {
  margin-top:16px;
  border:1px solid rgba(0,103,95,.12);
  background:#fffdf9;
  border-radius:28px;
  padding:26px;
  box-shadow:0 18px 44px rgba(15,23,42,.06);
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:18px;
}
.mlp-hero span{font-size:12px;letter-spacing:.18em;text-transform:uppercase;color:#00675f;font-weight:950}
.mlp-hero h1{margin:8px 0 8px;font-size:clamp(32px,6vw,54px);line-height:.95;font-weight:950;color:#0b1428;letter-spacing:-.06em}
.mlp-hero p{margin:0;max-width:640px;color:#64748b;font-weight:680;line-height:1.55}
.mlp-refresh{border:0;background:#00675f;color:#fff;border-radius:999px;padding:12px 16px;font-weight:900;display:inline-flex;align-items:center;gap:8px;cursor:pointer;box-shadow:0 14px 30px rgba(0,103,95,.22)}
.mlp-refresh:disabled{opacity:.6}

.mlp-card-actions{display:flex;align-items:center;justify-content:flex-end;gap:10px;flex-wrap:wrap}
.mlp-wallet-add-btn{
  border:0;
  border-radius:999px;
  padding:10px 14px;
  background:#0f172a;
  color:#fff;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 12px 26px rgba(15,23,42,.16);
  white-space:nowrap;
}
.mlp-wallet-add-btn:hover{transform:translateY(-1px)}
.mlp-wallet-modal .mlp-redemption-copy{max-width:420px}
.mlp-wallet-actions-modal{display:grid;gap:10px;width:100%;margin:14px 0 10px}
.mlp-wallet-provider-btn{
  width:100%;
  min-height:46px;
  border:0;
  border-radius:14px;
  display:flex;
  align-items:center;
  justify-content:center;
  text-decoration:none;
  font-weight:950;
  cursor:pointer;
}
.mlp-wallet-provider-btn.is-google{background:#111827;color:#fff}
.mlp-wallet-provider-btn.is-apple{background:#000;color:#fff}
.mlp-wallet-provider-btn.is-disabled{background:#eef2f7;color:#64748b;cursor:not-allowed}
.mlp-wallet-card-preview{
  width:100%;
  border:1px dashed #dbe4ea;
  border-radius:18px;
  padding:14px;
  display:grid;
  place-items:center;
  gap:8px;
  background:#f8fafc;
}
.mlp-wallet-card-preview small{color:#64748b;font-size:12px;font-weight:750;text-align:center}

.mlp-summary {
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:18px;
  margin: 0 0 18px;
}
.mlp-summary>div {
  min-height: 88px;
  border:1px solid #e4e9ef;
  background:rgba(255,255,255,.96);
  border-radius:18px;
  padding:18px 20px;
  box-shadow:0 16px 34px rgba(15,23,42,.05);
  display:grid;
  grid-template-columns:auto auto 1fr;
  align-items:center;
  gap:14px;
}
.mlp-summary svg {
  width: 46px;
  height: 46px;
  padding: 11px;
  color:#00675f;
  border-radius:999px;
  background:#e7f7f2;
}
.mlp-summary strong { font-size: 36px; line-height:1; font-weight:950; letter-spacing:-.045em; color:#0b1428; }
.mlp-summary span { color:#5b6b83; font-size:14px; font-weight:850; line-height:1.25; }

.mlp-grid { display:grid; grid-template-columns:1fr; gap:22px; }
.mlp-card {
  position:relative;
  overflow:hidden;
  border:1px solid #dbe4eb;
  background:#fff;
  border-radius:28px;
  padding:22px;
  box-shadow:0 20px 54px rgba(15,23,42,.06);
}
.mlp-option-one-pass {
  min-height: 350px;
  padding: 18px 22px 20px !important;
  border-color: rgba(0,103,95,.17) !important;
  border-top: 4px solid #00675f !important;
  background:
    linear-gradient(90deg, rgba(255,255,255,.94) 0%, rgba(255,255,255,.96) 66%, rgba(250,246,238,.78) 100%) !important;
}
.mlp-option-one-pass.has-reward { border-color:rgba(0,103,95,.30)!important; box-shadow:0 28px 70px rgba(0,103,95,.10); }

.mlp-option-one-pass::before {
  content:"";
  position:absolute;
  right: 260px;
  top: -60px;
  width: 260px;
  height: 520px;
  background: linear-gradient(90deg, transparent, rgba(247,242,234,.84), transparent);
  opacity:.65;
  transform: rotate(0deg);
  z-index:0;
}
.mlp-card-watermark {
  position:absolute;
  right: 82px !important;
  top: 38px !important;
  width: 330px !important;
  height: 260px !important;
  background-size:contain;
  background-position:center;
  background-repeat:no-repeat;
  opacity:.070 !important;
  filter: grayscale(.22) saturate(.75) contrast(1.08) !important;
  transform: rotate(-4deg) !important;
  z-index:0;
  mix-blend-mode:multiply;
}
.mlp-card-watermark::after {
  content:"";
  position:absolute;
  inset:-36px;
  background:radial-gradient(circle at center, transparent 0 36%, rgba(255,255,255,.42) 60%, rgba(255,255,255,.88) 92%);
}
.mlp-card-watermark-text {
  position:absolute;
  right:90px;
  top:82px;
  z-index:0;
  font-size:170px;
  line-height:.8;
  font-weight:950;
  letter-spacing:-.12em;
  color:rgba(0,103,95,.055);
  user-select:none;
}
.mlp-card-head,.mlp-programs,.mlp-actions { position:relative; z-index:3; }
.mlp-card-head-clean {
  display:grid !important;
  grid-template-columns:auto minmax(0,1fr) auto;
  gap:18px;
  align-items:center !important;
  padding:0 0 16px !important;
}
.mlp-business-logo {
  width: 82px;
  height: 66px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  overflow: hidden;
  flex: 0 0 auto;
  background: linear-gradient(135deg,#004b45,#00786f);
  color: #fff;
  border:1px solid rgba(0,103,95,.16);
  box-shadow:0 14px 28px rgba(0,103,95,.10);
}
.mlp-business-logo img { width:100%; height:100%; object-fit:cover; }
.mlp-business-logo span { font-size:18px; line-height:1; font-weight:850; letter-spacing:-.06em; }
.mlp-title { min-width:0; }
.mlp-title span {
  display:inline-flex;
  width:fit-content;
  border-radius:999px;
  padding:6px 10px;
  background:rgba(0,103,95,.075);
  border:1px solid rgba(0,103,95,.10);
  color:#00675f!important;
  letter-spacing:.14em!important;
  font-size:11px;
  font-weight:950;
  text-transform:uppercase;
}
.mlp-title h2 {
  margin:8px 0 5px!important;
  font-size:29px!important;
  line-height:1.02;
  letter-spacing:-.052em;
  font-weight:950;
  color:#0b1428;
}
.mlp-title p { margin:0; color:#6a788c; font-size:13px; font-weight:720; }
.mlp-title p b { color:#9aa8ba; font-weight:900; margin:0 7px; }
.mlp-business-link {
  min-height:46px;
  border-radius:999px;
  border:1px solid #dbe5ee;
  background:#fff;
  color:#0f172a;
  padding:0 18px;
  display:inline-flex;
  align-items:center;
  gap:12px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 12px 26px rgba(15,23,42,.055);
  white-space:nowrap;
}
.mlp-business-link span { color:#00675f; font-size:20px; line-height:1; }

.mlp-mini-stats { display:none!important; }

.mlp-programs {
  position:relative;
  z-index:3;
  display:grid;
  grid-template-columns:minmax(0, 1.05fr) minmax(0, .95fr);
  gap:24px;
  align-items:stretch;
}
.mlp-program {
  position:relative;
  min-height: 190px;
  border:1px solid rgba(218,226,235,.92);
  border-radius:18px;
  padding:22px 24px;
  background:rgba(255,255,255,.84);
  box-shadow:0 12px 28px rgba(15,23,42,.035);
}
.mlp-program-kicker {
  display:inline-flex;
  align-items:center;
  gap:9px;
  margin-bottom:18px;
  color:#00675f;
  font-size:11px;
  letter-spacing:.16em;
  text-transform:uppercase;
  font-weight:950;
}
.mlp-program-kicker span {
  width:28px;
  height:28px;
  border-radius:999px;
  border:2px solid rgba(0,103,95,.82);
  display:inline-grid;
  place-items:center;
  font-size:15px;
  line-height:1;
}
.mlp-program-title-row {
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:18px;
  margin-bottom:12px;
}
.mlp-program-title-row strong {
  color:#0b1428;
  font-size:18px;
  font-weight:950;
  letter-spacing:-.035em;
}
.mlp-program-title-row em {
  color:#00675f;
  font-size:23px;
  font-style:normal;
  font-weight:950;
  letter-spacing:-.04em;
  white-space:nowrap;
}
.mlp-point-stat-row {
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:18px;
  margin-bottom:19px;
}
.mlp-point-stat-row div { min-width:0; }
.mlp-point-stat-row strong {
  display:block;
  color:#0f766e;
  font-size:20px;
  font-weight:950;
  letter-spacing:-.04em;
  line-height:1;
}
.mlp-point-stat-row span {
  display:block;
  margin-top:6px;
  color:#64748b;
  font-size:12px;
  font-weight:760;
  line-height:1.2;
}
.mlp-point-pass {
  margin: 0 0 11px;
}
.mlp-point-track {
  height: 16px;
  border-radius:999px;
  background:#dfe9e8;
  overflow:hidden;
  box-shadow: inset 0 1px 2px rgba(15,23,42,.08);
}
.mlp-point-track i {
  display:block;
  height:100%;
  min-width:42px;
  border-radius:inherit;
  background:linear-gradient(90deg,#007d74,#0f9f91);
  box-shadow:0 8px 18px rgba(0,103,95,.18);
}
.mlp-point-marks {
  display:flex;
  justify-content:space-between;
  gap:8px;
  margin-top:8px;
  color:#93a2b5;
  font-size:11px;
  font-weight:850;
}
.mlp-point-marks span.is-active { color:#00675f; }
.mlp-program-bottom {
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  color:#64748b;
  font-size:12px;
  font-weight:820;
}
.mlp-program-bottom small {
  color:#64748b;
  font-size:12px;
  font-weight:850;
}
.mlp-program-note {
  margin-top:17px;
  display:flex;
  align-items:center;
  gap:10px;
  color:#64748b;
  font-size:13px;
  font-weight:720;
}
.mlp-program-note i {
  color:#00675f;
  font-style:normal;
  font-size:15px;
}
.mlp-stamp-layout {
  display:grid;
  grid-template-columns:160px minmax(0,1fr);
  gap:18px;
  align-items:start;
}
.mlp-stamp-copy strong {
  display:block;
  color:#00675f;
  font-size:25px;
  line-height:1;
  letter-spacing:-.04em;
  font-weight:950;
}
.mlp-stamp-copy span {
  display:block;
  margin-top:8px;
  color:#64748b;
  font-size:13px;
  font-weight:760;
}
.mlp-stamp-grid {
  display:grid;
  grid-template-columns:repeat(5, 48px);
  gap:16px 18px;
  align-items:center;
  justify-content:center;
  min-height:110px;
}
.mlp-stamp-grid.is-two-rows { grid-template-columns:repeat(5,48px); }
.mlp-stamp {
  width:44px;
  height:44px;
  border-radius:999px;
  display:grid;
  place-items:center;
  position:relative;
  overflow:hidden;
  border:1.5px dashed rgba(148,163,184,.50);
  color:#8b9aad;
  background:
    radial-gradient(circle at 30% 22%, rgba(255,255,255,.95), transparent 44%),
    linear-gradient(145deg, rgba(255,255,255,.84), rgba(242,246,247,.78));
  box-shadow: inset 0 1px 0 rgba(255,255,255,.86);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease;
}
.mlp-stamp::after {
  content:"";
  position:absolute;
  inset:6px;
  border-radius:inherit;
  border:1px solid rgba(148,163,184,.24);
  pointer-events:none;
}
.mlp-stamp small {
  font-size:11px;
  line-height:1;
  color:rgba(100,116,139,.72);
  font-weight:950;
  position:relative;
  z-index:2;
}
.mlp-stamp b {
  font-size:17px;
  line-height:1;
  color:currentColor;
  position:relative;
  z-index:3;
}
.mlp-stamp.is-filled {
  border-style:solid;
  border-color:#00675f;
  color:#ffffff;
  background:
    radial-gradient(circle at 30% 18%, rgba(255,255,255,.22), transparent 38%),
    linear-gradient(145deg,#00897f,#005f55);
  box-shadow:0 14px 26px rgba(0,103,95,.20);
  transform: translateY(-1px);
}
.mlp-stamp.is-filled::after {
  inset:5px;
  border-color:rgba(255,255,255,.38);
}
.mlp-stamp.is-filled.has-logo {
  background:#fff;
  border-width:2px;
  border-color:#00675f;
  box-shadow:0 14px 28px rgba(0,103,95,.20), inset 0 0 0 4px rgba(0,103,95,.07);
}
.mlp-stamp.is-filled.has-logo::before {
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(180deg, transparent 42%, rgba(0,103,95,.28));
  z-index:2;
  pointer-events:none;
}
.mlp-stamp.is-filled.has-logo::after {
  inset:4px;
  border-color:rgba(255,255,255,.62);
  z-index:3;
}
.mlp-stamp-logo {
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
  position:relative;
  z-index:1;
}
.mlp-stamp.is-filled.has-logo b,
.mlp-stamp.is-filled.has-logo small { display:none; }
.mlp-stamp-grid.has-reward-ready .mlp-stamp.is-filled {
  box-shadow:0 14px 28px rgba(0,103,95,.24), 0 0 0 5px rgba(0,103,95,.08);
}
.mlp-stamp-grid em {
  grid-column:1/-1;
  color:#64748b;
  font-style:normal;
  font-size:12px;
  font-weight:850;
}


.mlp-pass-status {
  margin: 0 0 14px;
  border: 1px solid rgba(0,103,95,.12);
  background: rgba(0,103,95,.055);
  border-radius: 18px;
  padding: 11px 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color:#00675f;
}
.mlp-pass-status.is-ready {
  border-color: rgba(0,103,95,.22);
  background: linear-gradient(135deg, rgba(0,103,95,.10), rgba(255,255,255,.82));
  box-shadow: 0 12px 28px rgba(0,103,95,.08);
}
.mlp-pass-status span {
  font-size: 12px;
  font-weight: 950;
}
.mlp-pass-status small {
  color:#64748b;
  font-size: 11px;
  font-weight: 850;
}

.mlp-reward-ready-line {
  margin-top: 10px;
  display:inline-flex;
  align-items:center;
  gap:7px;
  border-radius:999px;
  padding:8px 10px;
  background:#ecfdf5;
  color:#00675f;
  font-size:12px;
  font-weight:950;
}

.mlp-state,.mlp-error {
  border:1px solid #e2e8f0;
  background:#fff;
  border-radius:18px;
  padding:16px;
  margin-bottom:14px;
  color:#64748b;
  font-weight:850;
}
.mlp-error{border-color:#fecaca;background:#fff1f2;color:#991b1b}
.mlp-empty{
  text-align:center;
  border:1px solid #e2e8f0;
  border-radius:26px;
  background:#fff;
  padding:34px;
  box-shadow:0 16px 36px rgba(15,23,42,.055);
}
.mlp-empty div{width:62px;height:62px;border-radius:999px;background:#ecfdf5;color:#00675f;display:grid;place-items:center;margin:0 auto 14px}
.mlp-empty h2{margin:0;color:#0f172a;font-size:24px;letter-spacing:-.04em}
.mlp-empty p{color:#64748b;font-weight:700}
.mlp-empty button{border:0;background:#00675f;color:#fff;border-radius:999px;padding:12px 16px;font-weight:950;cursor:pointer}


.mlp-use-reward-btn {
  margin-top: 12px;
  width: fit-content;
  min-height: 38px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #00675f, #00897f);
  color: #fff;
  padding: 0 14px;
  font-size: 12px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(0,103,95,.18);
}
.mlp-redemption-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147482500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15,23,42,.42);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.mlp-redemption-modal {
  position: relative;
  width: min(100%, 430px);
  border-radius: 30px;
  border: 1px solid rgba(0,103,95,.16);
  background: #fffdf8;
  padding: 24px;
  text-align: center;
  box-shadow: 0 30px 90px rgba(15,23,42,.28);
}
.mlp-redemption-close {
  position:absolute;
  top: 14px;
  right: 14px;
  width: 38px;
  height: 38px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color:#334155;
  border-radius: 999px;
  display:grid;
  place-items:center;
  cursor:pointer;
}
.mlp-redemption-icon {
  width: 58px;
  height: 58px;
  margin: 0 auto 12px;
  border-radius: 20px;
  display:grid;
  place-items:center;
  color:#00675f;
  background: rgba(0,103,95,.10);
}
.mlp-redemption-eyebrow {
  display:block;
  color:#00675f;
  font-size: 11px;
  font-weight: 950;
  letter-spacing:.16em;
  text-transform: uppercase;
}
.mlp-redemption-modal h2 {
  margin: 8px 0 8px;
  color:#0f172a;
  font-size: 30px;
  line-height: 1;
  letter-spacing:-.05em;
  font-weight:950;
}
.mlp-redemption-copy {
  margin: 0 auto 16px;
  max-width: 330px;
  color:#64748b;
  font-size: 14px;
  line-height: 1.45;
  font-weight: 720;
}
.mlp-redemption-copy strong { color:#0f172a; }
.mlp-redemption-qr {
  width: 242px;
  height: 242px;
  margin: 0 auto;
  border-radius: 26px;
  border: 1px solid #e2e8f0;
  background: #fff;
  display:grid;
  place-items:center;
  box-shadow: inset 0 0 0 8px #fff, 0 18px 42px rgba(15,23,42,.08);
  overflow:hidden;
}
.mlp-redemption-meta {
  margin: 14px auto 0;
  display:inline-flex;
  align-items:center;
  gap: 7px;
  color:#00675f;
  font-size: 12px;
  font-weight: 900;
  background: rgba(0,103,95,.08);
  border: 1px solid rgba(0,103,95,.12);
  border-radius: 999px;
  padding: 8px 11px;
}
.mlp-redemption-open-app{
  min-height:43px;
  border:1px solid rgba(0,103,95,.22);
  background:#effdfa;
  color:#00675f;
  border-radius:999px;
  text-decoration:none;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:0 16px;
  margin-top:12px;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
}
.mlp-redemption-open-app:active{transform:scale(.98);}
.mlp-redemption-note {
  margin: 12px 0 0;
  color:#94a3b8;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 760;
}
.mlp-redemption-error {
  margin: 12px 0 0;
  border-radius: 14px;
  background:#fff1f2;
  color:#991b1b;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 850;
}
.mlp-redemption-loading {
  margin-top: 12px;
  color:#00675f;
  font-weight: 900;
  font-size: 12px;
}

@media (max-width: 980px) {
  .mlp-summary { grid-template-columns:1fr; }
  .mlp-programs { grid-template-columns:1fr; }
  .mlp-card-head-clean { grid-template-columns:auto minmax(0,1fr); }
  .mlp-business-link { grid-column:1/-1; width:fit-content; }
  .mlp-stamp-layout { grid-template-columns:1fr; }
  .mlp-stamp-grid { justify-content:start; }
}
@media (max-width: 640px) {
  .mlp-page:not(.mlp-page-embedded) { padding:0 0 92px; }
  .mlp-page-embedded { padding-bottom: 0; }
  .mlp-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(96px, 1fr));
    gap: 8px;
    overflow-x: auto;
    margin-bottom: 12px;
    padding-bottom: 2px;
    scrollbar-width: none;
  }
  .mlp-summary::-webkit-scrollbar { display: none; }
  .mlp-summary>div {
    min-height: 72px;
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 12px;
    border-radius: 16px;
  }
  .mlp-summary svg {
    width: 30px;
    height: 30px;
    padding: 7px;
  }
  .mlp-summary strong { font-size:24px; }
  .mlp-summary span { font-size: 11px; line-height: 1.15; }
  .mlp-grid { gap: 12px; }
  .mlp-card {
    border-radius:22px;
    padding:14px!important;
    min-height: 0 !important;
    box-shadow:0 12px 28px rgba(15,23,42,.055);
  }
  .mlp-option-one-pass::before,
  .mlp-card-watermark,
  .mlp-card-watermark-text { display: none !important; }
  .mlp-card-head-clean {
    grid-template-columns: auto minmax(0,1fr);
    gap: 12px;
    padding-bottom: 12px !important;
  }
  .mlp-business-logo {
    width: 54px;
    height: 48px;
    border-radius: 12px;
  }
  .mlp-title span {
    padding: 5px 8px;
    font-size: 9.5px;
  }
  .mlp-title h2 {
    margin-top: 6px!important;
    font-size:21px!important;
    line-height:1.05;
  }
  .mlp-title p {
    font-size: 11.5px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .mlp-business-link {
    grid-column: 1 / -1;
    width: 100%;
    min-height: 40px;
    justify-content: center;
    font-size: 12px;
    padding: 0 12px;
  }
  .mlp-programs { grid-template-columns:1fr; gap: 10px; }
  .mlp-pass-status { display:grid; gap:4px; }
  .mlp-program {
    min-height: 0;
    padding:14px;
    border-radius: 18px;
  }
  .mlp-program-title-row strong { font-size: 16px; }
  .mlp-stamp-layout { grid-template-columns:1fr; gap: 12px; }
  .mlp-stamp-copy strong { font-size: 28px; }
  .mlp-stamp-grid { justify-content:start; gap: 7px; }
  .mlp-stamp { width: 34px; height: 34px; }
  .mlp-point-stat-row { grid-template-columns:1fr; gap:8px; }
  .mlp-program-note { font-size: 11.5px; }
}

.mlp-redemption-link-mode {
  display: block;
  margin-top: 10px;
  color: #00675f;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: .08em;
  text-transform: uppercase;
}

/* Explicit tracks prevent intrinsic action widths from squeezing the business name. */
.mlp-card .mlp-card-head-clean{grid-template-columns:66px minmax(0,1fr)!important;width:100%;box-sizing:border-box;gap:14px!important}
.mlp-card-head-clean .mlp-business-logo{grid-column:1;grid-row:1;width:60px!important;height:60px!important}
.mlp-card-head-clean .mlp-title{grid-column:2;grid-row:1;width:auto!important;min-width:0!important;max-width:100%;text-align:left}
.mlp-card-head-clean .mlp-title h2{white-space:normal!important;overflow-wrap:anywhere;word-break:normal;max-width:100%;line-height:1.2!important}
.mlp-card-head-clean .mlp-title span{white-space:normal!important;display:inline-block;max-width:100%;box-sizing:border-box}
.mlp-card-head-clean .mlp-title p{display:block!important;-webkit-line-clamp:unset!important;white-space:normal!important;overflow:visible!important;line-height:1.5}
.mlp-card-head-clean .mlp-card-actions{grid-column:1/-1!important;grid-row:2;display:flex!important;flex-wrap:wrap;gap:10px;width:100%;min-width:0}
.mlp-card-head-clean .mlp-card-actions button{flex:1 1 160px;min-width:0;white-space:normal!important;justify-content:center;font-size:13px;min-height:44px;padding:10px 14px}
.mlp-redemption-modal .mlp-redemption-close{z-index:20!important;pointer-events:auto!important;min-width:44px;min-height:44px;touch-action:manipulation}
.mlp-redemption-close svg{pointer-events:none}
@media(max-width:600px){.mlp-card .mlp-card-head-clean{grid-template-columns:48px minmax(0,1fr)!important;gap:12px!important}.mlp-card-head-clean .mlp-business-logo{width:48px!important;height:48px!important}.mlp-card-head-clean .mlp-title h2{font-size:20px!important}.mlp-card .mlp-programs{grid-template-columns:minmax(0,1fr)!important}.mlp-program{min-width:0;box-sizing:border-box}}
`;
